import { apiClient } from './api'

export interface Role {
  id: number
  name: string
  description?: string | null
  permissions?: Permission[]
}

export interface Permission {
  id: number
  name: string
  description?: string | null
}

export interface User {
  id: number
  email: string
  full_name?: string | null
  is_active: boolean
  is_superuser: boolean
  roles: Role[]
  created_at?: string
  updated_at?: string
}

export interface UserCreate {
  email: string
  password: string
  full_name?: string
  is_active?: boolean
  role_ids?: number[]
}

export interface UserUpdate {
  email?: string
  password?: string
  full_name?: string
  is_active?: boolean
  role_ids?: number[]
}

export const usersService = {
  /**
   * List users
   */
  async list(params?: { skip?: number; limit?: number }): Promise<User[]> {
    const response = await apiClient.get<User[]>('/api/v1/users', { params })
    return response.data
  },

  /**
   * List users who can be assigned to a task (gated on task:read, supports search).
   */
  async assignable(params?: { q?: string; limit?: number }): Promise<User[]> {
    const response = await apiClient.get<User[]>('/api/v1/users/assignable', { params })
    return response.data
  },

  /**
   * Get user by ID
   */
  async get(id: number): Promise<User> {
    const response = await apiClient.get<User>(`/api/v1/users/${id}`)
    return response.data
  },

  /**
   * Create user
   */
  async create(data: UserCreate): Promise<User> {
    const response = await apiClient.post<User>('/api/v1/users', data)
    return response.data
  },

  /**
   * Update user
   */
  async update(id: number, data: UserUpdate): Promise<User> {
    const response = await apiClient.put<User>(`/api/v1/users/${id}`, data)
    return response.data
  },

  /**
   * Delete user
   */
  async delete(id: number): Promise<void> {
    await apiClient.delete(`/api/v1/users/${id}`)
  },
}

export const rolesService = {
  /**
   * List roles
   */
  async list(): Promise<Role[]> {
    const response = await apiClient.get<Role[]>('/api/v1/roles')
    return response.data
  },

  /**
   * Get role by ID
   */
  async get(id: number): Promise<Role> {
    const response = await apiClient.get<Role>(`/api/v1/roles/${id}`)
    return response.data
  },
}

